import {
  Component,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  NgZone,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { fadeInOut } from '../../animations/animations';
import { AboutSummaryComponent } from '../../components/about-summary/about-summary.component';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Same SSR guard as home.component.ts — ScrollTrigger touches window/document
// as soon as it's registered, and gsap.registerPlugin is idempotent, so this
// is safe to repeat per lazy-loaded route rather than relying on Home having
// run first.
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

@Component({
  selector: 'app-about',
  standalone: true,
  imports: [CommonModule, AboutSummaryComponent],
  templateUrl: './about.component.html',
  styleUrls: ['./about.component.scss'],
  animations: [fadeInOut]
})
export class AboutComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private zone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  @ViewChild('joinTitle') private joinTitleRef?: ElementRef<HTMLElement>;

  private joinScrollTrigger?: ScrollTrigger;
  private joinDecodeTweens: gsap.core.Tween[] = [];

  // Glyphs the decode effect flickers through before settling on the
  // real character. Kept mono-width-ish and terminal/glitchy on purpose —
  // it's meant to read as "decoding", not as a typo.
  private readonly SCRAMBLE_CHARS = '!<>-_\\/[]{}=+*^?#$%01';

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupJoinTitleDecode();
    }
  }

  ngOnDestroy(): void {
    this.joinScrollTrigger?.kill();
    this.joinDecodeTweens.forEach((tween) => tween.kill());
  }

  /**
   * Kinetic-typography reveal for "Join Our Community": a third animation
   * mechanic alongside the home page's scroll-scrubbed hero/manifesto
   * (continuous, tied to scroll position) and magnetic cursor (continuous,
   * tied to pointer position). This one is scroll-*triggered* rather than
  * scroll-*scrubbed* — it fires whenever the heading crosses into view
  * from either direction — and plays out as a per-character decode: each letter flickers
   * through scramble glyphs before landing on the real character, staggered
   * left to right like a terminal resolving a string.
   *
   * Respects `prefers-reduced-motion` by doing nothing and leaving the
  * plain heading text in place. The heading keeps its real text as an
   * aria-label so screen readers get "Join Our Community" regardless of
  * whether the decode runs — the per-letter spans are aria-hidden. The
  * reveal replays whenever the heading enters the viewport from either
  * direction.
   */
  private setupJoinTitleDecode(): void {
    const title = this.joinTitleRef?.nativeElement;
    if (!title) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    const letters = this.splitJoinTitle(title);
    if (!letters.length) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.joinScrollTrigger = ScrollTrigger.create({
        trigger: title,
        start: 'top 85%',
        onEnter: () => this.runDecodeReveal(letters),
        onEnterBack: () => this.runDecodeReveal(letters)
      });
    });
  }

  /** Wraps each character of the title in its own span, same approach as
   * the home hero's letter-by-letter split, so the decode can animate each
   * one independently. Spaces are preserved as non-breaking so wrapping
   * doesn't shift once the spans are in place. */
  private splitJoinTitle(titleEl: HTMLElement): Array<{ el: HTMLElement; char: string }> {
    const text = titleEl.textContent ?? '';
    if (!text.trim()) {
      return [];
    }

    titleEl.setAttribute('aria-label', text);
    titleEl.textContent = '';

    const letters: Array<{ el: HTMLElement; char: string }> = [];
    for (const char of text) {
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      if (char === ' ') {
        span.className = 'decode-letter decode-letter--space';
        span.textContent = '\u00A0';
      } else {
        span.className = 'decode-letter';
        span.textContent = char;
      }
      titleEl.appendChild(span);
      letters.push({ el: span, char });
    }
    return letters;
  }

  private runDecodeReveal(letters: Array<{ el: HTMLElement; char: string }>): void {
    this.joinDecodeTweens.forEach((tween) => tween.kill());
    this.joinDecodeTweens = [];

    letters.forEach(({ el, char }, i) => {
      if (char === ' ') {
        return;
      }
      el.classList.add('is-decoding');
      const state = { t: 0 };
      const tween = gsap.to(state, {
        t: 1,
        duration: 0.5,
        delay: i * 0.035,
        ease: 'none',
        onUpdate: () => {
          el.textContent = state.t < 0.8
            ? this.SCRAMBLE_CHARS[Math.floor(Math.random() * this.SCRAMBLE_CHARS.length)]
            : char;
        },
        onComplete: () => {
          el.textContent = char;
          el.classList.remove('is-decoding');
        }
      });
      this.joinDecodeTweens.push(tween);
    });
  }

  async navigateToSubscribe() {
    await this.router.navigate(['/'], { fragment: 'subscription-section' });
    setTimeout(() => {
      const element = document.getElementById('subscription-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
}